import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { FileExplorer } from './FileExplorer';
import { MetadataCodeEditor } from './CodeEditor/MetadataCodeEditor';
import { CloseConfirmDialog } from './CloseConfirmDialog';
import { QuickOpen } from './QuickOpen';
import { FilterModal } from './FilterModal';
import { TabContentSyncer } from './TabContentSyncer';
import { useMetadataItem, useActiveBranch } from '@/hooks/metadata_v1';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { useMetadataEditorStore } from '@/stores/metadataEditorStore';
import { openFilePersistently } from './openFilePersistently';
import type { FileNode, PanelSizes } from '@/components/molecules/metadata_v1';
import { getAncestorFolderIds } from '@/constants/metadata/bundleTypes';
import {
  buildImproveWithAIPromptForMetadataItem,
  getSecurityFindingsForMetadataItem,
} from '@/services/security-scanner';
import type { MetadataType } from '@/services/metadata_v1/types';
import { useChatPendingInputStore } from '@/stores/chatPendingInputStore';
import { useMultiAgentIdeStore } from '@/pages/multi-agent/ide/store/multiAgentIdeStore';

const DEFAULT_PANEL_SIZES: PanelSizes = {
  left: 280,
  middle: 600,
  right: 300,
};

const MIN_PANEL_SIZES = {
  left: 200,
};

const MAX_PANEL_SIZES = {
  left: 500,
};

interface EditorLayoutProps {
  /** Raw metadata types from GetMetadataTypes query (folders with counts) */
  metadataTypes?: MetadataType[] | null;
  isLoadingFolders?: boolean;
  /** Called after a file is saved successfully — e.g. to trigger post-save analysis */
  onFileSaved?: (data: { id: string; fileName: string; metadataType: string }) => void;
  /** When set, layout renders an error state instead of the editor (e.g. metadata load error) */
  error?: Error | null;
  /** True when the org can start an initial metadata fetch. */
  hasNoRetrieval?: boolean;
  /** Start initial metadata fetch and connect to progress SSE */
  onStartInitialFetch?: () => void | Promise<void>;
  /** True while initial fetch is in progress (disables Fetch metadata button) */
  isFetchingMetadata?: boolean;
  /** Controlled open state for QuickOpen — lifted to page level so the top bar can trigger it */
  isQuickOpenOpen?: boolean;
  onQuickOpenChange?: (open: boolean) => void;
}

export function EditorLayout({
  metadataTypes,
  isLoadingFolders = false,
  onFileSaved,
  error,
  hasNoRetrieval = false,
  onStartInitialFetch,
  isFetchingMetadata = false,
  isQuickOpenOpen: externalQuickOpenOpen,
  onQuickOpenChange,
}: EditorLayoutProps) {
  const navigate = useNavigate();
  const setMultiAgentIntent = useChatPendingInputStore((s) => s.setMultiAgentIntent);
  const userId = useAuthStoreV1((state) => state.user?.id || "");
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const orgId = selectedOrg?.id || "";
  const [panelSizes, setPanelSizes] = useState<PanelSizes>(DEFAULT_PANEL_SIZES);
  const [pendingFile, setPendingFile] = useState<FileNode | null>(null);
  const [internalQuickOpenOpen, setInternalQuickOpenOpen] = useState(false);
  const isQuickOpenOpen = externalQuickOpenOpen ?? internalQuickOpenOpen;
  const setIsQuickOpenOpen = onQuickOpenChange ?? setInternalQuickOpenOpen;
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // Transform API data to FileNode structure (folders for explorer)
  const files = useMemo<FileNode[]>(() => {
    if (!metadataTypes || metadataTypes.length === 0) return [];
    return metadataTypes.map((type) => ({
      id: type.metadataType,
      name: type.metadataType,
      type: 'folder' as const,
      path: `/${type.metadataType}`,
      metadataType: type.metadataType,
      count: type.count,
      children: [],
    }));
  }, [metadataTypes]);

  const handleToggleFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  // Metadata type names for filter modal and counts
  const metadataTypeNames = useMemo(
    () => metadataTypes?.map((t) => t.metadataType) ?? [],
    [metadataTypes]
  );

  // Store actions
  const openFile = useMetadataEditorStore((state) => state.openFile);
  const setTabLoading = useMetadataEditorStore((state) => state.setTabLoading);
  const syncTabContentFromServer = useMetadataEditorStore((state) => state.syncTabContentFromServer);
  const activeTabId = useMetadataEditorStore((state) => state.activeTabId);
  const tabs = useMetadataEditorStore((state) => state.tabs);
  const getTabByFileId = useMetadataEditorStore((state) => state.getTabByFileId);
  const visibleMetadataTypes = useMetadataEditorStore((state) => state.visibleMetadataTypes);
  const setBundleSiblingsForTab = useMetadataEditorStore((state) => state.setBundleSiblingsForTab);

  // Registry of bundle folder ID → child FileNodes, populated as the tree expands.
  // A plain ref keeps writes off the render path — bundle nav strips read from tabs (store) not this ref.
  const bundleChildrenRef = useRef<Map<string, FileNode[]>>(new Map());

  // Auto-reveal: when the active tab changes, expand its ancestor folders so the
  // file is visible in the explorer without the user having to navigate manually.
  useEffect(() => {
    const tab = useMetadataEditorStore.getState().getActiveTab();
    if (!tab?.file) return;
    const ancestorIds = getAncestorFolderIds(tab.file);
    if (ancestorIds.length === 0) return;
    setExpandedFolders((prev) => {
      const needsUpdate = ancestorIds.some((id) => !prev.has(id));
      if (!needsUpdate) return prev; // stable ref — prevents unnecessary re-render
      const next = new Set(prev);
      ancestorIds.forEach((id) => next.add(id));
      return next;
    });
  }, [activeTabId]); // runs only on tab switch, not on content changes

  // Bundle children registry: called by FileExplorer when the tree loads a bundle folder's children.
  // Back-fills siblings on any already-open tabs that belong to the same bundle (handles QuickOpen).
  const handleBundleChildrenLoaded = useCallback(
    (folderId: string, children: FileNode[]) => {
      bundleChildrenRef.current.set(folderId, children);
      const { tabs: currentTabs, setBundleSiblingsForTab: setSiblings } =
        useMetadataEditorStore.getState();
      currentTabs.forEach((tab) => {
        if (getAncestorFolderIds(tab.file).includes(folderId)) {
          setSiblings(tab.id, children.filter((s) => s.id !== tab.file.id));
        }
      });
    },
    [] // stable — refs and store access don't need deps
  );
  
  // Get selected file from active tab for highlighting in explorer
  const activeTab = tabs.find(t => t.id === activeTabId);
  const selectedFile = activeTab?.file || null;
  
  // Filter files based on visible metadata types
  // null = show all (default), Set = show only those types (empty Set = hide all)
  const filteredFiles = useMemo(() => {
    // null means show all (default state)
    if (visibleMetadataTypes === null) return files;
    // Empty set means hide all
    if (visibleMetadataTypes.size === 0) return [];
    
    return files.filter(file => {
      // For folders (metadata types), check if they're in visible set
      if (file.type === 'folder') {
        return visibleMetadataTypes.has(file.name);
      }
      // For files, check their metadataType
      return file.metadataType && visibleMetadataTypes.has(file.metadataType);
    });
  }, [files, visibleMetadataTypes]);
  
  // Fetch file content when a file is pending to be opened
  const { data: fileContent, isLoading: isLoadingContent, isError: isContentError, error: contentError } = useMetadataItem(
    pendingFile?.id,
    { enabled: !!pendingFile && pendingFile.type === 'file' }
  );

  const setTabError = useMetadataEditorStore((state) => state.setTabError);
  const setBranchForTab = useMetadataEditorStore((state) => state.setBranchForTab);
  const setMainContentForTab = useMetadataEditorStore((state) => state.setMainContentForTab);

  // Check for an existing active branch whenever a file is pending to be opened
  const { data: activeBranchData, isLoading: isLoadingBranch } = useActiveBranch(
    pendingFile?.id,
    { enabled: !!pendingFile && pendingFile.type === 'file' }
  );

  // When content (and branch check) is loaded, sync into tab and clear loading state
  useEffect(() => {
    if (!pendingFile) return;
    const tab = getTabByFileId(pendingFile.id);
    if (isContentError && tab) {
      setTabError(tab.id, contentError ?? new Error('Failed to load file'));
      setTabLoading(tab.id, false);
      setPendingFile(null);
      return;
    }
    if (fileContent && !isLoadingContent && !isLoadingBranch && tab) {
      const mainContent = fileContent.content || '';
      // If an active branch exists, show branch content in the editor
      const displayContent = activeBranchData ? (activeBranchData.content || '') : mainContent;
      syncTabContentFromServer(pendingFile.id, displayContent, fileContent.isEdited ?? false);
      // Always cache the main content for the "View Main" toggle
      setMainContentForTab(tab.id, mainContent);
      // Store the branch (or null) on the tab
      setBranchForTab(tab.id, activeBranchData ?? null);
      setTabLoading(tab.id, false);
      setPendingFile(null);
    }
  }, [pendingFile, fileContent, isLoadingContent, isContentError, contentError, activeBranchData, isLoadingBranch, getTabByFileId, syncTabContentFromServer, setTabLoading, setTabError, setBranchForTab, setMainContentForTab]);

  const handleLeftResize = useCallback((_e: unknown, data: { size: { width: number } }) => {
    setPanelSizes((prev) => ({ ...prev, left: data.size.width }));
  }, []);

  const handleFileSelect = useCallback((file: FileNode) => {
    if (file.type !== 'file') return;

    // Check if file is already open
    const existingTab = getTabByFileId(file.id);
    if (existingTab) {
      // Just switch to it
      useMetadataEditorStore.getState().setActiveTab(existingTab.id);
      return;
    }

    // Open tab immediately with empty content and loading state; fetch content
    openFile(file, '', true, false);
    const tab = getTabByFileId(file.id);
    if (tab) {
      setTabLoading(tab.id, true);
      // Populate bundle siblings from registry if already loaded (e.g. user expanded tree first)
      const segments = file.path.split('/').filter(Boolean);
      if (segments.length >= 3) {
        const bundleFolderId = `${segments[0]}/${segments[1]}`;
        const siblings = bundleChildrenRef.current.get(bundleFolderId);
        if (siblings) {
          setBundleSiblingsForTab(tab.id, siblings.filter((s) => s.id !== file.id));
        }
      }
    }
    setPendingFile(file);
  }, [getTabByFileId, openFile, setTabLoading, setBundleSiblingsForTab]);

  /**
   * Double-click in the tree — open the file for good.
   *
   * The rule lives in `openFilePersistently`; `handleFileSelect` is handed to it
   * as the preview open, so the double-click path reuses the same fetch, loading
   * and bundle-sibling flow a single click uses. The browser fires `click` before
   * `dblclick`, so in practice the preview is already open and only promotion
   * happens — no re-fetch, and nothing that could disturb unsaved edits.
   */
  const handleFileOpen = useCallback(
    (file: FileNode) => openFilePersistently(file, { openPreview: handleFileSelect }),
    [handleFileSelect],
  );

  const handleImproveWithAI = useCallback(async () => {
    if (!activeTab) return;
    const file = activeTab.file;
    const metadataType = file.metadataType || "ApexClass";
    const fallbackPayload = {
      pendingFile: { id: file.id, name: file.name },
      metadataType,
      initialMessage:
        `Improve ${file.name}. Focus on security, bulkification, error handling, and maintainability. Return ONE complete code block.`,
    };

    // Seed the multi-agent with the file's DB UUID + content so the chat loads the
    // file DIRECTLY instead of re-searching by name (token waste) — mirrors the
    // SF-releases handoff. The UUID is the key the agent uses to skip the search.
    if (file.id) {
      useMultiAgentIdeStore.getState().setPendingRefactorFile({
        id: String(file.id),
        name: file.name,
        content: fileContent?.content || "",
        metadataType,
      });
    }

    try {
      if (orgId && userId && file.id) {
        const findingsItem = await getSecurityFindingsForMetadataItem({
          org_id: orgId,
          user_id: userId,
          metadata_item_id: String(file.id),
          file_name: file.name,
          metadata_type: metadataType,
        });

        if (findingsItem?.findings?.length) {
          // Route the improve handoff to the MULTI-AGENT (not dev-agent): set the
          // pending intent the MultiAgentRoute consumes on mount, then navigate.
          setMultiAgentIntent(buildImproveWithAIPromptForMetadataItem(findingsItem));
          navigate("/weave");
          return;
        }
      }
    } catch (err) {
      console.warn("[metadata-improve-with-ai] findings lookup failed", err);
      toast.error("Could not load issues for this file. Opening Improve with AI with a generic prompt.");
    }

    setMultiAgentIntent(fallbackPayload.initialMessage);
    navigate("/weave");
  }, [activeTab, navigate, setMultiAgentIntent, orgId, userId]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-destructive">
          Error loading metadata: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full gap-1 overflow-hidden bg-muted/20">
      {/* Sync React Query data to Zustand for open tabs */}
      <TabContentSyncer />
      
      {/* Close Confirmation Dialog */}
      <CloseConfirmDialog />

      {/* Filter Modal (Cmd+Shift+F) */}
      <FilterModal
        metadataTypes={metadataTypeNames}
        hasNoRetrieval={hasNoRetrieval}
        onFetchMetadata={onStartInitialFetch}
        isFetchingMetadata={isFetchingMetadata}
      />
      
      {/* Quick Open (Cmd+P) */}
      <QuickOpen 
        files={files} 
        onFileSelect={handleFileSelect}
        isOpen={isQuickOpenOpen}
        onOpenChange={setIsQuickOpenOpen}
      />
      
      {/* Left Panel - File Explorer */}
      <ResizableBox
        width={Math.min(panelSizes.left, typeof window !== "undefined" ? window.innerWidth * 0.3 : panelSizes.left)}
        height={Infinity}
        axis="x"
        minConstraints={[MIN_PANEL_SIZES.left, Infinity]}
        maxConstraints={[MAX_PANEL_SIZES.left, Infinity]}
        onResize={handleLeftResize}
        resizeHandles={['e']}
        handle={
          <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 transition-colors z-10" />
        }
      >
        <div className="h-full border border-border rounded-lg overflow-hidden bg-card">
          <FileExplorer
            files={filteredFiles}
            isLoading={isLoadingFolders}
            selectedFile={selectedFile}
            expandedFolders={expandedFolders}
            onFileSelect={handleFileSelect}
            onFileOpen={handleFileOpen}
            onToggleFolder={handleToggleFolder}
            onOpenQuickOpen={() => setIsQuickOpenOpen(true)}
            totalMetadataTypes={metadataTypeNames.length}
            hasNoRetrieval={hasNoRetrieval}
            onFetchMetadata={onStartInitialFetch}
            isFetchingMetadata={isFetchingMetadata}
            revealFileId={selectedFile?.id}
            onBundleChildrenLoaded={handleBundleChildrenLoaded}
          />
        </div>
      </ResizableBox>

      {/* Middle Panel - Code Editor (powered by @ncoder/code-editor package) */}
      <div className="flex-1 min-h-0 min-w-0 border border-border rounded-lg overflow-hidden bg-card">
        <MetadataCodeEditor
          onOpenAIPanel={handleImproveWithAI}
          isAIPanelOpen={false}
          onFileSaved={onFileSaved}
          onFileSelect={handleFileSelect}
        />
      </div>
    </div>
  );
}
